/**
 * Regras puras do gump de saque. Sem React — `tests/loot.mjs` importa direto.
 */
import { MOCK_ITEM_CATALOG, pesoDoItem } from '../api/itemCatalog';
import { baseDe } from '../look/materiais';

export const FAMILIAS_CHIP = ['equip', 'cons', 'reag', 'outro'] as const;
export type FamiliaLoot = 'prata' | (typeof FAMILIAS_CHIP)[number];
export type FiltroLoot = 'tudo' | FamiliaLoot;

export const FILTRO_STORAGE = 'uoidle.loot.filtro';
export const LIMIAR_CHIPS = 6;
export const LIMIAR_BUSCA = 12;
export const LIMIAR_BUSCA_ESTREITA = 20;
export const FECHAR_VAZIO_MS = 1500;

export interface LootPilha {
  readonly id: string;
  readonly defId: string;
  readonly qty: number;
}

const GRUPO_POR_ID = new Map<string, string>();
for (const g of MOCK_ITEM_CATALOG.groups) {
  for (const it of g.items) GRUPO_POR_ID.set(it.id, g.id);
}

const EQUIP = new Set(['weapon', 'armor', 'shield', 'cloth', 'mount']);
const CONS_ID = new Set(['bandage', 'empty-bottle']);

export function grupoDoItem(defId: string): string | undefined {
  return GRUPO_POR_ID.get(defId) ?? GRUPO_POR_ID.get(baseDe(defId));
}

export function familiaDoLoot(defId: string): FamiliaLoot {
  if (defId === 'silver' || defId === 'gold') return 'prata';
  if (CONS_ID.has(defId)) return 'cons';
  const g = grupoDoItem(defId);
  if (g && EQUIP.has(g)) return 'equip';
  if (g === 'potion') return 'cons';
  return 'outro';
}

export function pesoUnDoItem(defId: string): number {
  return pesoDoItem(defId) ?? 0;
}

export function quantoCabe(qty: number, pesoUn: number, peso: number, pesoMax: number): number {
  if (!(qty > 0)) return 0;
  if (!(pesoUn > 0)) return qty;
  const folga = pesoMax - peso;
  if (!(folga > 0)) return 0;
  const cabe = Math.floor((folga + 1e-9) / pesoUn);
  if (cabe < 1) return 0;
  return Math.min(qty, cabe);
}

export function visiveis(
  slots: readonly LootPilha[],
  filtro: FiltroLoot,
  busca: string,
  nomeDe: (defId: string) => string
): LootPilha[] {
  const q = busca.trim().toLocaleLowerCase();
  const filtrados = slots.filter(s => {
    if (filtro !== 'tudo' && familiaDoLoot(s.defId) !== filtro) return false;
    if (q && !nomeDe(s.defId).toLocaleLowerCase().includes(q)) return false;
    return true;
  });
  return [
    ...filtrados.filter(s => s.defId === 'silver'),
    ...filtrados.filter(s => s.defId !== 'silver')
  ];
}

export function contagemFamilias(slots: readonly LootPilha[]): Record<FamiliaLoot, number> {
  const c: Record<FamiliaLoot, number> = { prata: 0, equip: 0, cons: 0, reag: 0, outro: 0 };
  for (const s of slots) c[familiaDoLoot(s.defId)] += 1;
  return c;
}

export function projecao(
  lista: readonly LootPilha[],
  peso: number,
  pesoMax: number,
  pesoUnDe: (defId: string) => number = pesoUnDoItem
): { entram: number; ficam: number; parciais: number; pesoEntra: number } {
  let atual = peso;
  let entram = 0;
  let ficam = 0;
  let parciais = 0;
  let pesoEntra = 0;
  for (const it of lista) {
    const un = pesoUnDe(it.defId);
    const cabe = quantoCabe(it.qty, un, atual, pesoMax);
    if (cabe === 0) { ficam += 1; continue; }
    entram += 1;
    if (cabe < it.qty) parciais += 1;
    const add = un * cabe;
    atual += add;
    pesoEntra += add;
  }
  return { entram, ficam, parciais, pesoEntra };
}

export function limiarChips(n: number): boolean {
  return n >= LIMIAR_CHIPS;
}

export function limiarBusca(n: number, estreita: boolean): boolean {
  return n >= (estreita ? LIMIAR_BUSCA_ESTREITA : LIMIAR_BUSCA);
}

export function filtroValido(raw: string | null | undefined): FiltroLoot | null {
  if (raw === 'tudo' || raw === 'prata' || raw === 'equip' || raw === 'cons' || raw === 'reag' || raw === 'outro')
    return raw;
  return null;
}

export type KindPrazo = 's' | 'clock' | 'min' | 'h';

export function partesDoPrazo(ms: number): { pouco: boolean; kind: KindPrazo; n: number; clock: string } {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const pouco = s <= 10;
  if (s >= 3600) return { pouco, kind: 'h', n: Math.ceil(s / 3600), clock: '' };
  if (s >= 600) return { pouco, kind: 'min', n: Math.ceil(s / 60), clock: '' };
  if (s >= 60) {
    const m = Math.floor(s / 60);
    return { pouco, kind: 'clock', n: s, clock: `${m}:${String(s % 60).padStart(2, '0')}` };
  }
  return { pouco, kind: 's', n: s, clock: '' };
}
